"use client";

import { useState, useEffect, useContext } from "react";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "@/components/ui/pagination";
import {
  Star,
  ThumbsUp,
  ThumbsDown,
  Calendar,
  Home,
  Filter,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  MessageCircle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/components/property-review/atoms/AuthStateHandler";
import { submitReviewVote, getReviewVoteStats, type VoteStats } from "@/app/actions/review-vote-actions";
import { formatCurrencyWithAbbreviation } from "@/lib/utils/format-currency";
import { toast } from "sonner";
import Image from "next/image";

// StarRating component
const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={16}
          className={
            star <= Math.round(rating)
              ? 'text-yellow-400 fill-yellow-400'
              : 'text-gray-200 fill-gray-200'
          }
        />
      ))}
      <span className="ml-2 text-sm font-medium">{rating.toFixed(1)}</span>
    </div>
  );
};

// Database-driven reviews instead of mock data
// Reviews are now fetched via server actions from review-data-actions.ts

const REVIEWS_PER_PAGE = 3;

interface ReviewsListProps {
  reviews: Array<{
    id: string;
    tenantId: string;
    tenantName: string;
    tenancyDuration: string;
    tenancyPeriod: string;
    moveInDate: string;
    moveOutDate: string;
    isVerified: boolean;
    overallRating: number;
    ratings: Record<string, number>;
    review: string;
    pros: string[];
    cons: string[];
    wouldRecommend: boolean;
    createdAt: Date;
    helpfulCount: number;
    unhelpfulCount: number;
    reviewPhotos: string[];
    unitType: string;
    floorPlan: string;
    monthlyRent: string;
    rentIncrease: string | null;
    rentPrice?: number;
    rentCurrency?: string;
  }>
}

const ReviewsList = ({ reviews = [] }: ReviewsListProps) => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'positive' | 'negative' | 'verified'>('all');
  const [sortOrder, setSortOrder] = useState<'latest' | 'oldest' | 'helpful'>('latest');
  const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});
  const [photoView, setPhotoView] = useState<string | null>(null);
  const [voteStats, setVoteStats] = useState<Record<string, VoteStats>>({});
  const [votingLoading, setVotingLoading] = useState<Record<string, boolean>>({});
  
  const { isAuthenticated, user } = useAuth();

  // Filter reviews
  const filteredReviews = reviews.filter(review => {
    if (filter === 'all') return true;
    if (filter === 'positive') return review.overallRating >= 4;
    if (filter === 'negative') return review.overallRating < 3;
    if (filter === 'verified') return review.isVerified;
    return true;
  });

  // Sort reviews
  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (sortOrder === 'latest') {
      return b.createdAt.getTime() - a.createdAt.getTime();
    } else if (sortOrder === 'oldest') {
      return a.createdAt.getTime() - b.createdAt.getTime();
    } else if (sortOrder === 'helpful') {
      return b.helpfulCount - a.helpfulCount;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedReviews.length / REVIEWS_PER_PAGE);
  const paginatedReviews = sortedReviews.slice(
    (page - 1) * REVIEWS_PER_PAGE,
    page * REVIEWS_PER_PAGE
  );

  const handlePageChange = (newPage: number) => {
    if (newPage === page) return;
    setLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setPage(newPage);
      setLoading(false);
    }, 400);
  };

  const toggleExpandReview = (reviewId: string) => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  // Format date to more readable form
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };

  // Fetch vote stats for all reviews
  useEffect(() => {
    const fetchVoteStats = async () => {
      const stats: Record<string, VoteStats> = {};
      for (const review of reviews) {
        try {
          const reviewStats = await getReviewVoteStats(review.id, user?.id);
          stats[review.id] = reviewStats;
        } catch (error) {
          console.error(`Error fetching vote stats for review ${review.id}:`, error);
          stats[review.id] = { helpful_count: 0, unhelpful_count: 0 };
        }
      }
      setVoteStats(stats);
    };

    if (reviews.length > 0) {
      fetchVoteStats();
    }
  }, [reviews, user?.id]);

  // Handle voting
  const handleVote = async (reviewId: string, isHelpful: boolean) => {
    if (!isAuthenticated) {
      toast.error('Please log in to vote on reviews');
      return;
    }

    setVotingLoading(prev => ({ ...prev, [reviewId]: true }));

    try {
      const result = await submitReviewVote(reviewId, isHelpful);
      
      if (result.success) {
        // Update local vote stats
        const updatedStats = await getReviewVoteStats(reviewId, user?.id);
        setVoteStats(prev => ({
          ...prev,
          [reviewId]: updatedStats
        }));
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to submit vote');
    } finally {
      setVotingLoading(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 relative">
      {/* Filter and Sort Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 relative inline-block">
            Tenant Reviews
            <span className="absolute -bottom-1 left-0 w-full h-1.5 bg-blue-100 rounded-full"></span>
          </h2>
          <p className="text-gray-500 mt-2">
            Real experiences from verified previous tenants
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-1">
                <Filter size={14} />
                Filter
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { setFilter('all'); setPage(1); }}
                className={filter === 'all' ? 'bg-blue-50 text-blue-600' : ''}>
                All Reviews
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setFilter('positive'); setPage(1); }}
                className={filter === 'positive' ? 'bg-blue-50 text-blue-600' : ''}>
                Positive Only (4+ stars)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setFilter('negative'); setPage(1); }}
                className={filter === 'negative' ? 'bg-blue-50 text-blue-600' : ''}>
                Critical Reviews (&lt; 3 stars)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { setFilter('verified'); setPage(1); }}
                className={filter === 'verified' ? 'bg-blue-50 text-blue-600' : ''}>
                <CheckCircle size={14} className="mr-2" />
                Verified Tenants Only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Select 
            defaultValue="latest"
            value={sortOrder}
            onValueChange={(value) => {
              setSortOrder(value as 'latest' | 'oldest' | 'helpful');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest">Most Recent</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="helpful">Most Helpful</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Review Count Summary */}
      <div className="flex items-center gap-2 mb-6">
        <Badge variant="outline" className="bg-blue-50 text-blue-600 font-medium px-2 py-1 rounded-md">
          {filteredReviews.length} {filteredReviews.length === 1 ? 'Review' : 'Reviews'}
        </Badge>
        {filter !== 'all' && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setFilter('all');
              setPage(1);
            }}
            className="h-7 px-2 text-xs"
          >
            Clear Filter
          </Button>
        )}
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <span className="text-sm text-gray-500">Loading reviews...</span>
          </div>
        </div>
      ) : paginatedReviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-6 bg-gray-50 rounded-lg border border-gray-100">
          <AlertCircle className="h-12 w-12 text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No reviews found</h3>
          <p className="text-gray-500 text-center max-w-md mb-6">
            {filter !== 'all' 
              ? "No reviews match your current filter criteria. Try changing your filter options."
              : "There are no tenant reviews for this property yet. Be the first to share your experience!"}
          </p>
          {filter !== 'all' && (
            <Button 
              variant="outline" 
              onClick={() => {
                setFilter('all');
                setPage(1);
              }}
            >
              View All Reviews
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {paginatedReviews.map((review) => (
            <div 
              key={review.id}
              className={`bg-white border ${review.overallRating >= 4 ? 'border-green-100' : review.overallRating < 3 ? 'border-red-100' : 'border-gray-100'} rounded-xl shadow-sm transition-all duration-200 hover:shadow-md p-5`}
            >
              {/* Review Header - Mobile Optimized */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-3 sm:gap-4">
                <div className="flex-1">
                  {/* Rating and Verification - Simplified for Mobile */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <div className={`px-2 py-1 rounded text-sm font-medium ${
                      review.overallRating >= 4 
                        ? 'bg-green-100 text-green-700' 
                        : review.overallRating >= 3 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {review.overallRating.toFixed(1)}
                    </div>
                    {/* Hide star rating on mobile to reduce redundancy */}
                    <div className="hidden sm:flex">
                      <StarRating rating={review.overallRating} />
                    </div>
                    {review.isVerified && (
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200 flex gap-1 items-center">
                        <CheckCircle size={10} className="fill-blue-600" />
                        <span className="text-[10px] uppercase tracking-wider font-semibold">Verified</span>
                      </Badge>
                    )}
                  </div>
                  
                  {/* Tenancy and Property Info */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} className="shrink-0" />
                      <span>Stayed: {review.tenancyPeriod}</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-1">
                      <Home size={12} />
                      <span>{review.unitType}</span>
                    </div>
                  </div>
                </div>
                
                {/* Review Date and Rent - Mobile Optimized */}
                <div className="text-right text-sm text-gray-500 sm:shrink-0">
                  <div className="mb-1">{formatDate(review.createdAt)}</div>
                  {(review.rentPrice || review.monthlyRent !== 'Not disclosed') && (
                    <div className="font-medium text-gray-700">
                      {review.rentPrice && review.rentCurrency 
                        ? formatCurrencyWithAbbreviation(review.rentPrice, review.rentCurrency)
                        : review.monthlyRent !== 'Not disclosed' 
                        ? review.monthlyRent 
                        : 'No data'}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Review Content */}
              <div className={`relative ${!expandedReviews[review.id] && review.review.length > 250 ? 'max-h-24 overflow-hidden' : ''}`}>
                <p className="text-gray-700">
                  {review.review}
                </p>
                {!expandedReviews[review.id] && review.review.length > 250 && (
                  <div className="absolute bottom-0 left-0 right-0 h-10 bg-linear-to-t from-white to-transparent"></div>
                )}
              </div>
              
              {review.review.length > 250 && (
                <button
                  onClick={() => toggleExpandReview(review.id)}
                  className="text-sm text-blue-600 hover:text-blue-800 mt-2 flex items-center gap-1"
                >
                  {expandedReviews[review.id] ? 'Show less' : 'Read more'}
                  <ChevronDown 
                    size={14} 
                    className={`transition-transform ${expandedReviews[review.id] ? 'rotate-180' : ''}`} 
                  />
                </button>
              )}
              
              {/* Pros and Cons */}
              {(review.pros.length > 0 || review.cons.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                  {review.pros.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1">
                        <ThumbsUp size={14} className="fill-green-700" />
                        Pros
                      </h4>
                      <ul className="space-y-1">
                        {review.pros.map((pro, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <div className="mt-1">•</div>
                            <div>{pro}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {review.cons.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
                        <ThumbsDown size={14} className="fill-red-700" />
                        Cons
                      </h4>
                      <ul className="space-y-1">
                        {review.cons.map((con, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <div className="mt-1">•</div>
                            <div>{con}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Review Photos */}
              {review.reviewPhotos.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Photos from tenant</h4>
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {review.reviewPhotos.map((photo, idx) => (
                      <div 
                        key={idx} 
                        className="relative h-20 w-20 shrink-0 rounded-md overflow-hidden cursor-pointer"
                        onClick={() => setPhotoView(photo)}
                      >
                        <Image
                          fill
                          src={photo}
                          alt={`Review photo ${idx + 1} by tenant`}
                          sizes="80px"
                          quality={75}
                          className="object-cover hover:opacity-90 transition-opacity"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/placeholder.svg";
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Review Footer */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 pt-3 border-t border-gray-100 gap-3 sm:gap-0">
                <div className="flex gap-2 items-center flex-wrap">
                  <Badge variant={review.wouldRecommend ? "outline" : "secondary"} className="flex items-center gap-1">
                    {review.wouldRecommend ? (
                      <>
                        <ThumbsUp size={12} className="text-green-600" />
                        <span className="text-xs">Would recommend</span>
                      </>
                    ) : (
                      <>
                        <ThumbsDown size={12} className="text-red-600" />
                        <span className="text-xs">Would not recommend</span>
                      </>
                    )}
                  </Badge>
                  {review.rentIncrease && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        review.rentIncrease.startsWith('+') 
                          ? 'bg-red-50 text-red-700 border-red-200' 
                          : review.rentIncrease.startsWith('-')
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : review.rentIncrease.toLowerCase().includes('no increase') || review.rentIncrease.toLowerCase().includes('stable')
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-gray-50 text-gray-700 border-gray-200'
                      }`}
                    >
                      Rent: {review.rentIncrease}
                    </Badge>
                  )}
                </div>
                
                {/* Voting System */}
                <div className="flex items-center justify-between sm:justify-end gap-4">
                  <div className="flex items-center gap-3">
                    {/* Thumbs Up */}
                    <button 
                      onClick={() => handleVote(review.id, true)}
                      disabled={votingLoading[review.id] || !isAuthenticated}
                      className={`flex items-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        voteStats[review.id]?.user_vote === true 
                          ? 'text-green-600 bg-green-50 px-2 py-1 rounded-md' 
                          : 'text-gray-500 hover:text-green-600 hover:bg-green-50 px-2 py-1 rounded-md'
                      }`}
                      title={!isAuthenticated ? "Login to vote" : "Mark as helpful"}
                    >
                      <ThumbsUp size={14} />
                      <span>{voteStats[review.id]?.helpful_count || 0}</span>
                    </button>
                    
                    {/* Thumbs Down */}
                    <button 
                      onClick={() => handleVote(review.id, false)}
                      disabled={votingLoading[review.id] || !isAuthenticated}
                      className={`flex items-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        voteStats[review.id]?.user_vote === false 
                          ? 'text-red-600 bg-red-50 px-2 py-1 rounded-md' 
                          : 'text-gray-500 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-md'
                      }`}
                      title={!isAuthenticated ? "Login to vote" : "Mark as not helpful"}
                    >
                      <ThumbsDown size={14} />
                      <span>{voteStats[review.id]?.unhelpful_count || 0}</span>
                    </button>
                  </div>
                  
                  {!isAuthenticated && (
                    <span className="text-xs text-gray-400 hidden sm:inline">
                      Login to vote
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination className="mt-8">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={e => { e.preventDefault(); if (!loading && page > 1) handlePageChange(page - 1); }}
                aria-disabled={page === 1 || loading}
                className={(page === 1 || loading) ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
            {[...Array(totalPages)].map((_, i) => (
              <PaginationItem key={i} className="hidden sm:block">
                <PaginationLink
                  href="#"
                  isActive={page === i + 1}
                  onClick={e => { e.preventDefault(); if (!loading) handlePageChange(i + 1); }}
                  aria-disabled={loading}
                  className={loading ? 'pointer-events-none opacity-50' : ''}
                >
                  {i + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={e => { e.preventDefault(); if (!loading && page < totalPages) handlePageChange(page + 1); }}
                aria-disabled={page === totalPages || loading}
                className={(page === totalPages || loading) ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      
      {/* Photo Viewer Modal */}
      {photoView && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setPhotoView(null)}
        >
          <div className="relative max-w-4xl max-h-[80vh] w-full">
            <button 
              className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 z-10"
              onClick={() => setPhotoView(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="relative w-full h-full flex items-center justify-center">
              <Image
                src={photoView}
                alt="Review photo full view"
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                quality={85}
                className="object-contain rounded-lg shadow-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/placeholder.svg";
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsList;
